import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import TopNavBarComponent from "../../components/nav/TopNavBarComponent";
import { OutlineCard, WaringAlertBox } from "../../components/Components";
import { auth } from "../../../App";

const Container = styled.div`
	position: relative;
	min-height: 100%;
`;

const TutorialContent = styled.section`
	position: relative;
	max-width: 1200px;
	margin: 48px auto 0;
	z-index: 1;

	@media screen and (max-width: 400px) {
		margin-top: 24px;
	}

	.row {
		display: flex;
		gap: 12px;
		flex-wrap: wrap;
		margin: 24px 0;
	}

	figure img {
		max-height: 200px;
		border-radius: 6px;
		object-fit: contain;
		cursor: zoom-in;
	}

	figure img + figcaption {
		font-weight: var(--md-sys-typescale-label-medium-font-weight);
		font-size: var(--md-sys-typescale-label-medium-font-size);
		letter-spacing: var(--md-sys-typescale-label-medium-tracking);
		line-height: var(--md-sys-typescale-label-medium-height);
		text-transform: var(--md-sys-typescale-label-medium-text-transform);
		text-decoration: var(--md-sys-typescale-label-medium-text-decoration);
		color: var(--color-on-surface-variant);
		font-style: italic;
	}
`;

export default function TutorialExplainPage() {
	const params = useParams();

	const pageTitle = params.tutorialId
		.replaceAll("_", " ")
		.replace(/^\w/, (c) => c.toUpperCase());

	console.log(params.tutorialId);

	return (
		<Container>
			<TopNavBarComponent
				title={pageTitle}
				centredTitle={true}
				goBack={true}
			/>
			{params.tutorialId === "prenotazione" && tutorialBooking()}
			{params.tutorialId === "arrivo" && tutorialEnter()}
			{params.tutorialId === "partenza" && tutorialExit()}
			{params.tutorialId === "camino_e_stufe" && tutorialFireplace()}
			{params.tutorialId === "acqua" && tutorialWater()}
			{params.tutorialId === "casa" && tutorialHouse()}
		</Container>
	);

	function tutorialBooking() {
		return (
			<TutorialContent>
				<p>
					Per prenotare la casa di Corzoneso tramite l'app (o il sito
					web), naviga nel menu e seleziona la voce "Calendario". Qui
					potrai indicare la data di inizio e la data di fine del tuo
					soggiorno. Dopo averle selezionate, apparirà un riassunto:
					in questa fase, avrai la possibilità di modificare le date o
					specificare se la prenotazione riguarda manutenzione/lavori.
					Infine, clicca sul pulsante "Prenota" per completare la
					procedura.
				</p>
				<OutlineCard style={{ margin: "24px 0" }} notHover>
					<h3>Costi della prenotazione</h3>
					<ul style={{ margin: "6px 1em" }}>
						<li>Costo per notte: CHF 50.-</li>
						<li>Costo minimo della prenotazione: CHF 75.-</li>
					</ul>
					<p style={{ fontStyle: "italic" }}>
						Nota bene: Il riepilogo finale mostrerà il costo del
						soggiorno. Le spese di corrente saranno calcolate al
						check-out.
					</p>
				</OutlineCard>
				<p>
					Se ti è più comodo segui l'elenco puntato qui sotto per
					sapere quali passaggi fare:
				</p>
				<ol>
					<li>
						Accedi al <Link to="/app/calendario">Calendario</Link>.
					</li>
					<li>
						Clicca sulla data di inizio e di fine della tua
						prenotazione.
					</li>
					<li>
						Controlla che sia tutto corretto nel riepilogo sotto al
						calendario
					</li>
					<li>
						Clicca sul pulsante "Prenota" per confermare la
						prenotazione.
					</li>
				</ol>
				<p style={{ marginTop: "24px" }}>
					Puoi modificare o annullare la tua prenotazione cliccando
					sulla prenotazione dal calendario, oppure passando per{" "}
					<Link to="/app/gestione">Gestione</Link>, inserendo la data
					della prenotazione e cliccando su "Annulla prenotazione"
				</p>
				<p style={{ marginTop: "24px" }}>
					Per il prossimo passo devi essere a Corzoneso, ma puoi
					consultare prima il tutorial{" "}
					<Link to="/app/tutorial/arrivo">Arrivo</Link> per essere
					sicuro/a di inserire i dati corretti.
				</p>
			</TutorialContent>
		);
	}

	function tutorialEnter() {
		return (
			<TutorialContent>
				<p>
					Appena arrivi a casa, la prima cosa è accendere la corrente
					<sup>1</sup> e aprire l'acqua (dai un'occhiata alle foto qui
					sotto per capire come fare!). Poi, quando è tutto ok, fai il
					check-in sull'app (o sul sito). Trova la pagina "Gestione"
					nel menu, lì vedrai la tua prenotazione e potrai anche
					aggiungere qualche appunto se serve. In questa pagina ti
					chiederemo di segnare quanti kilowatt segna il contatore
					della corrente, così alla fine sappiamo quanto hai
					consumato. Anche per questo, ci sono delle foto che ti
					spiegano come fare, non preoccuparti! Trovi un campo per i
					kW diurni<sup>2</sup> e notturni<sup>3</sup>, basta inserire
					le cifre che trovi sul display spostandoti col pulsante
					<sup>2/3</sup>.
				</p>
				<div className="row">
					<figure>
						<img
							src="/image/tutorial/quadro_elettrico.jpg"
							alt="quadro elettrico"
						/>
						<figcaption>1: Quadro elettrico</figcaption>
					</figure>
					<ol>
						<li>Accensione generale</li>
						<li>Boiler (Ricordati di prima aprire l'acqua!)</li>
						<li>Riscaldamento</li>
					</ol>
				</div>
				<p>
					Come già riportato sopra, una volta accesa la corrente in
					casa puoi già aggiungere i kW, le immagini qui sotto
					mostrano come spostarsi tra kW diurni e notturni.
				</p>
				<div className="row">
					<figure>
						<img
							src="/image/tutorial/contatore1.jpg"
							alt="Contatore diurno"
						/>
						<figcaption>2: Contatore diurno</figcaption>
					</figure>
					<figure>
						<img
							src="/image/tutorial/contatore2.jpg"
							alt="Contatore notturno"
						/>
						<figcaption>3: Contatore notturno</figcaption>
					</figure>
				</div>
				<p>
					Una volta salvato il check-in nell'app o sul sito l'unica
					cosa che devi fare è goderti la vacanza!
				</p>
			</TutorialContent>
		);
	}

	function tutorialExit() {
		return (
			<TutorialContent>
				<p>
					Prima di salutare la casa, fai un ultimo giro per
					assicurarti di non aver dimenticato niente di tuo! Per fare
					il check-out, apri l'app (o il sito web) e vai nella sezione
					"Gestione". Trovando la tua prenotazione, vedrai una comoda
					lista di cose da controllare prima di partire: spunta ogni
					voce man mano che verifichi. Una volta controllato tutto,
					inserisci nuovamente i valori dei kilowatt del contatore
					(come hai fatto all'arrivo)<sup>1/2</sup>. Infine, chiudi
					l'acqua e assicurati che tutto sia spento prima di partire.
				</p>
				<div className="row">
					<figure>
						<img
							src="/image/tutorial/contatore1.jpg"
							alt="Contatore diurno"
						/>
						<figcaption>1: Contatore diurno</figcaption>
					</figure>
					<figure>
						<img
							src="/image/tutorial/contatore2.jpg"
							alt="Contatore notturno"
						/>
						<figcaption>2: Contatore notturno</figcaption>
					</figure>
				</div>
				<div className="row">
					<figure>
						<img
							src="/image/tutorial/quadro_elettrico.jpg"
							alt="quadro elettrico"
						/>
						<figcaption>3: Quadro elettrico</figcaption>
					</figure>
					<ol>
						<li>Accensione generale</li>
						<li>Boiler (Ricordati di prima aprire l'acqua!)</li>
						<li>Riscaldamento</li>
					</ol>
				</div>
				<p>
					L'unica cosa che manca per terminare la prenotazione è il
					pagamento. Sempre in{" "}
					<Link to="/app/gestione">Gestione</Link> Puoi registrare il
					pagamento in 3 modi:
				</p>
				<ul>
					<li>Conto bancario</li>
					<li>Twint</li>
					<li>Buono weekend (se disponibile)</li>
				</ul>
				<p style={{ marginTop: "12px" }}>
					Una volta terminato il pagamento potrari rivedere le tue
					prenotazioni in{" "}
					<Link to="/app/impostazioni">Impostazioni</Link> -{">"}{" "}
					<Link to={`/app/prenotazioni/${auth.currentUser.uid}`}>
						Prenotazioni
					</Link>
				</p>
			</TutorialContent>
		);
	}

	function tutorialFireplace() {
		return (
			<TutorialContent>
				<p>
					Prima della partenza, ti preghiamo di seguire queste
					semplici istruzioni per lasciare in ordine il camino, la
					stufa a pellet e la stufa elettrica.
				</p>
				<article>
					<h2>Camino</h2>
					<p>
						<b>All'arrivo:</b> Per riscaldare rapidamente la casa
						con il calore del camino, apri le bocchette dell'aria.
						Puoi monitorare la temperatura interna del camino
						tramite il display a parete<sup>1</sup>.
					</p>
					<figure>
						<img
							src="/image/tutorial/disply_camino.jpg"
							alt="Display camino"
						/>
						<figcaption>1: Display camino</figcaption>
					</figure>
					<p style={{ marginTop: "24px" }}>
						<b>Alla partenza:</b> Prima di lasciare la casa, se il
						bracere contiene molta cenere, svuotalo. Inoltre, riempi
						la cesta dei legni, così che chi arriverà dopo di te
						possa riscaldare facilmente la casa avendo la legna a
						portata di mano.
					</p>
				</article>
				<article>
					<h2>Stufa a pellet</h2>
					<p>
						Prima di andare, svuota la cenere e pulisci il braciere
						prima di partire. Se il pellet è scarso o finito,
						lascialo scritto nelle note del check-out per avvisare
						chi arriverà dopo.
					</p>
				</article>
				<article>
					<h2>Stufa elettrica</h2>
					<p>
						Puoi controllare la temperatura della stufa elettrica
						con la rotella accanto al display del camino<sup>1</sup>
						.
					</p>
					<WaringAlertBox>
						<span className="material-symbol fill">warning</span>
						<span>
							<b>Attenzione!</b>
							<br />
							Non lasciare cose direttamente sopra la stufa ma
							utilizzare il legno posto sopra.{" "}
							<b>
								Ricordati di spegnere la stufa dopo una mezz'ora
								che è in funzione, se sta accesa troppo a lungo
								potrebbe bruciare la resistenza e quindi non
								funzionare più.
							</b>
						</span>
					</WaringAlertBox>
				</article>
			</TutorialContent>
		);
	}

	function tutorialWater() {
		return (
			<TutorialContent>
				<p>
					La valvola principale per l'apertura e la chiusura
					dell'acqua<sup>1</sup> si trova nello stanzino vicino al quadro
					elettrico.{" "}
					<b>Quando chiudi la valvola al momento della partenza</b>,
					ricordati di aprire tutti i rubinetti e tirare gli scarichi
					dei bagni per svuotare le tubature. In questo modo eviterai
					di lasciare acqua in circolo.{" "}
					<b>Quando invece arrivi e apri la valvola</b>, potresti
					trovare dei rubinetti aperti: assicurati di chiuderli tutti.
				</p>
				<WaringAlertBox>
					<span className="material-symbol fill">warning</span>
					<span>
						<b>Attenzione!</b>
						<br />
						Ricorda inoltre di accendere il boiler solo dopo aver
						aperto la valvola principale dell'acqua.
					</span>
				</WaringAlertBox>
				<figure style={{marginTop: "24px"}}>
					<img
						src="/image/tutorial/valvola_acqua.jpg"
						alt="Valvola dell'acqua"
					/>
					<figcaption>1: Valvola dell'acqua</figcaption>
				</figure>
			</TutorialContent>
		);
	}

	function tutorialHouse() {
		return <TutorialContent>Non ancora creato</TutorialContent>;
	}
}
